import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { GTCheckBox, GTColumnSelectAnchor, GTExpander, GTFrameworkCell } from 'src/components/GenericTable';
import { formatDate, formatNumber } from 'src/utils/misc';
import { filter } from 'lodash';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { gTCheckHandler } from 'src/components/GenericTable/util';
import { Link } from 'react-router-dom';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TableIds } from 'src/api/types/misc';
import { OpenInNew } from '@mui/icons-material';
import { TLLink } from 'src/pages/DashboardPage/comps';
import { TextOverflowTooltip } from 'src/common';
import { ControlsQuery } from 'src/graphql/_generated';
import { getMaturityLevelName } from 'src/api/types';

type Control = ControlsQuery['controls'][number];

type UseColumns = (a: { data?: Control[]; linkedControlIds: string[]; scenarioId: string }) => {
  columns: ColumnDef<Control>[];
  GTColumnSelectAnchorExported: JSX.Element;
  checked: string[];
  resetChecked: () => void;
};

const defaultColumns = ['strategy', 'name', 'owner', 'framework_controls', 'status', 'updated_at'];

export const useColumns: UseColumns = ({ data = [], linkedControlIds, scenarioId }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(
    `${TableIds.scenarioLinkExistingControl}-columns`,
    defaultColumns,
  );
  const [checked, setChecked] = useState<string[]>([]);

  // Helper function to check if a control is linked to the current scenario
  const isControlLinked = (controlId: string) => linkedControlIds.includes(controlId);

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.expander, GTInternalIds.tdm, GTInternalIds.checker];

    const list: ColumnDef<Control>[] = [
      {
        id: GTInternalIds.checker,
        header: ({ table }) => {
          const { isAllChecked, checkAllHandler, visibleRows } = gTCheckHandler(
            {
              checked,
              selector: 'id',
              table,
            },
            setChecked,
          );
          const isAllLinked = visibleRows.every((el) => isControlLinked(el));

          return (
            <GTCheckBox isChecked={isAllChecked || isAllLinked} onClick={checkAllHandler} disabled={isAllLinked} />
          );
        },
        cell: ({ row }) => {
          const id = row.original.id;
          const isLinked = isControlLinked(id);
          return (
            <GTCheckBox
              isChecked={checked.includes(id) || isLinked}
              onClick={() => {
                if (checked.includes(id)) {
                  setChecked(checked.filter((el) => el !== id));
                } else {
                  setChecked([...checked, id]);
                }
              }}
              disabled={isLinked}
            />
          );
        },
        size: 1,
        maxSize: 35,
        enableSorting: false,
      },

      {
        id: GTInternalIds.expander,
        header: () => null,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <GTExpander onClick={row.getToggleExpandedHandler()} isExpanded={row.getIsExpanded()} />
          ) : null;
        },
        size: 1,
        maxSize: 20,
        enableSorting: false,
      },
      {
        id: 'ux_id',
        header: 'Id',
        accessorFn: (row) => row.id || null,
        cell: (info) => info.getValue(),
      },
      {
        id: 'strategy',
        header: 'Strategy',
        accessorFn: (row) => row.strategy || null,
        cell: (info) => info.getValue(),
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.name,
        cell: (info) => (
          <TextOverflowTooltip>
            <Link
              className="table-link"
              to={`/risk-management/control/${info.row.original.id}`}
              target="_blank"
              onClick={(e) => {
                e.stopPropagation();
                mpEvent(MPEvents.Link, {
                  source: { value: ['Scenario', 'Modal:LinkControlsToScenario'], params: { id: scenarioId } },
                  destination: { value: ['Control'], params: { id: info.row.original.id } },
                  tags: ['RMP', 'SCENARIO'],
                });
              }}
            >
              {info.getValue() as string}
            </Link>
          </TextOverflowTooltip>
        ),
        sortingFn: sortingFns.text,
      },
      {
        id: 'owner',
        header: 'Owner',
        accessorFn: (row) => row.ownerName,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'framework_controls',
        header: 'Framework',
        cell: (info) => <GTFrameworkCell controls={info.row.original.frameworkControls} />,
        sortingFn: sortingFns.text,
      },
      {
        id: 'annual_cost',
        header: 'Annual Cost',
        accessorFn: (row) => row.annualCost || null,
        cell: ({ row }) => formatNumber(row.original.annualCost, { symbol: true }),
        sortingFn: sortingFns.alphanumeric,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => row.status || null,
        cell: (info) => info.getValue(),
      },
      {
        id: 'existingMaturity',
        header: 'Existing Maturity',
        cell: ({ row }) => getMaturityLevelName(row.original.existingMaturity),
      },
      {
        id: 'targetMaturity',
        header: 'Target Maturity',
        cell: ({ row }) => getMaturityLevelName(row.original.targetMaturity),
      },
      {
        id: 'updated_at',
        header: 'Last Edited',
        accessorFn: (row) => row.meta?.updatedAt || null,
        cell: ({ row }) => {
          const date = row.original.meta?.updatedAt;
          if (!date) return null;
          return (
            <TLLink date={date} newPage>
              {formatDate(date, { formatType: 'datetime' })}
            </TLLink>
          );
        },
      },

      {
        id: 'source',
        header: 'Link',
        accessorFn: (row) => row.linkUrl || null,
        cell: (info) => {
          const url = info.getValue();
          const linkText = (info.row.original.linkText || url) as string;
          return url ? (
            <a
              href={url as string}
              target="_blank"
              rel="noopener noreferrer"
              className="table-link"
              onClick={(e) => e.stopPropagation()}
            >
              {linkText}{' '}
              <OpenInNew
                css={`
                  font-size: 14px !important;
                  color: inherit;
                `}
              />
            </a>
          ) : (
            <span>{linkText}</span>
          );
        },
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: () => null,
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, checked, linkedControlIds, scenarioId]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.scenarioLinkExistingControl}
      options={[
        { key: 'ux_id', title: 'Id' },
        { key: 'strategy', title: 'Strategy' },
        { key: 'name', title: 'Name' },
        { key: 'owner', title: 'Owner' },
        { key: 'framework_controls', title: 'Framework' },
        { key: 'annual_cost', title: 'Annual Cost' },
        { key: 'status', title: 'Status' },
        { key: 'existingMaturity', title: 'Existing Maturity' },
        { key: 'targetMaturity', title: 'Target Maturity' },
        { key: 'updated_at', title: 'Last Edited' },
        { key: 'source', title: 'Link' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return {
    columns,
    GTColumnSelectAnchorExported,
    checked,
    resetChecked: () => setChecked([]),
  };
};
